import { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";

const countryCodes = [
  { code: "+60", country: "MY", flag: "🇲🇾", name: "Malaysia" },
  { code: "+65", country: "SG", flag: "🇸🇬", name: "Singapore" },
  { code: "+62", country: "ID", flag: "🇮🇩", name: "Indonesia" },
  { code: "+66", country: "TH", flag: "🇹🇭", name: "Thailand" },
  { code: "+63", country: "PH", flag: "🇵🇭", name: "Philippines" },
  { code: "+84", country: "VN", flag: "🇻🇳", name: "Vietnam" },
  { code: "+673", country: "BN", flag: "🇧🇳", name: "Brunei" },
  { code: "+95", country: "MM", flag: "🇲🇲", name: "Myanmar" },
  { code: "+856", country: "LA", flag: "🇱🇦", name: "Laos" },
  { code: "+855", country: "KH", flag: "🇰🇭", name: "Cambodia" },
  { code: "+1", country: "US", flag: "🇺🇸", name: "United States" },
  { code: "+44", country: "GB", flag: "🇬🇧", name: "United Kingdom" },
  { code: "+61", country: "AU", flag: "🇦🇺", name: "Australia" },
  { code: "+91", country: "IN", flag: "🇮🇳", name: "India" },
  { code: "+86", country: "CN", flag: "🇨🇳", name: "China" },
  { code: "+81", country: "JP", flag: "🇯🇵", name: "Japan" },
  { code: "+82", country: "KR", flag: "🇰🇷", name: "South Korea" },
  { code: "+971", country: "AE", flag: "🇦🇪", name: "UAE" },
  { code: "+966", country: "SA", flag: "🇸🇦", name: "Saudi Arabia" },
  { code: "+49", country: "DE", flag: "🇩🇪", name: "Germany" },
  { code: "+33", country: "FR", flag: "🇫🇷", name: "France" },
];

// Parse a phone string into { countryCode, localNumber }
function parsePhone(phone) {
  if (!phone) return { countryCode: "+60", localNumber: "" };
  const cleaned = phone.replace(/\s/g, "");
  // Try matching with + prefix
  if (cleaned.startsWith("+")) {
    // Sort by longest code first to match +673 before +6
    const sorted = [...countryCodes].sort((a, b) => b.code.length - a.code.length);
    for (const c of sorted) {
      if (cleaned.startsWith(c.code)) {
        return { countryCode: c.code, localNumber: cleaned.slice(c.code.length) };
      }
    }
    // Unknown code, try to extract
    const match = cleaned.match(/^\+(\d{1,4})(.*)/);
    if (match) return { countryCode: "+" + match[1], localNumber: match[2] };
  }
  // No country code — default to Malaysia +60
  // If starts with 0, strip leading 0 (Malaysian local format)
  const local = cleaned.startsWith("0") ? cleaned.slice(1) : cleaned;
  return { countryCode: "+60", localNumber: local };
}

// Combine country code + local into a full phone string
function combinePhone(countryCode, localNumber) {
  const cleaned = localNumber.replace(/[^0-9]/g, "");
  if (!cleaned) return "";
  return countryCode + cleaned;
}

export function PhoneInput({ value, onChange, className = "", "data-testid": testId }) {
  const { countryCode: initCode, localNumber: initLocal } = parsePhone(value);
  const [selectedCode, setSelectedCode] = useState(initCode);
  const [localNum, setLocalNum] = useState(initLocal);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  // Sync when external value changes (e.g., on lead load)
  useEffect(() => {
    const { countryCode, localNumber } = parsePhone(value);
    setSelectedCode(countryCode);
    setLocalNum(localNumber);
  }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Focus search on open
  useEffect(() => {
    if (dropdownOpen && searchRef.current) searchRef.current.focus();
  }, [dropdownOpen]);

  const selectedEntry = countryCodes.find(c => c.code === selectedCode) || { flag: "🏳️", code: selectedCode, country: "??" };

  const filtered = search
    ? countryCodes.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || c.code.includes(search) || c.country.toLowerCase().includes(search.toLowerCase()))
    : countryCodes;

  const handleCodeChange = (code) => {
    setSelectedCode(code);
    setDropdownOpen(false);
    setSearch("");
    onChange(combinePhone(code, localNum));
  };

  const handleLocalChange = (e) => {
    const val = e.target.value.replace(/[^0-9]/g, "");
    setLocalNum(val);
    onChange(combinePhone(selectedCode, val));
  };

  return (
    <div className={`flex items-center gap-0 ${className}`} data-testid={testId}>
      {/* Country code selector */}
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setDropdownOpen(!dropdownOpen)}
          className="flex items-center gap-1 h-9 px-2.5 border border-gray-300 border-r-0 rounded-l-lg bg-gray-50 hover:bg-gray-100 transition-colors text-sm shrink-0"
          data-testid={testId ? `${testId}-code-btn` : undefined}
        >
          <span className="text-base leading-none">{selectedEntry.flag}</span>
          <span className="text-xs text-gray-700 font-medium">{selectedCode}</span>
          <ChevronDown className="w-3 h-3 text-gray-400" />
        </button>
        {dropdownOpen && (
          <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-hidden" data-testid={testId ? `${testId}-dropdown` : undefined}>
            <div className="p-1.5 border-b border-gray-100">
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search country..."
                className="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-md outline-none focus:border-gray-400"
                data-testid={testId ? `${testId}-search` : undefined}
              />
            </div>
            <div className="overflow-y-auto max-h-48">
              {filtered.map(c => (
                <button
                  key={c.code + c.country}
                  type="button"
                  onClick={() => handleCodeChange(c.code)}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-gray-50 transition-colors ${selectedCode === c.code ? "bg-blue-50 text-blue-700" : "text-gray-700"}`}
                >
                  <span className="text-sm">{c.flag}</span>
                  <span className="flex-1 text-left">{c.name}</span>
                  <span className="text-gray-400 font-mono">{c.code}</span>
                </button>
              ))}
              {filtered.length === 0 && <p className="text-xs text-gray-400 text-center py-3">No match</p>}
            </div>
          </div>
        )}
      </div>
      {/* Local number input */}
      <input
        type="tel"
        value={localNum}
        onChange={handleLocalChange}
        placeholder="123456789"
        className="flex-1 h-9 px-3 border border-gray-300 rounded-r-lg text-sm outline-none focus:ring-1 focus:ring-[#0D0D5B] focus:border-[#0D0D5B] min-w-0"
        data-testid={testId ? `${testId}-local` : undefined}
      />
    </div>
  );
}

export { parsePhone, combinePhone, countryCodes };
